import { isValid, parseISO } from "date-fns";
import { formatZarDisplay } from "../services/currencyFormatterService";
import type { ApiPayLink } from "../services/payLinksService";
import type {
  PaymentLinkRow,
  PaymentLinkStatus,
} from "../types/paymentLinksPanel";

function parseApiAmount(raw: ApiPayLink["amount"]): number {
  if (raw == null) {
    return 0;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }
  if (
    typeof raw === "object" &&
    raw !== null &&
    "$numberDecimal" in raw &&
    typeof (raw as { $numberDecimal: unknown }).$numberDecimal === "string"
  ) {
    const n = parseFloat((raw as { $numberDecimal: string }).$numberDecimal);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function formatAmountDisplay(amount: number, currency: string): string {
  const code = (currency || "ZAR").toUpperCase();
  if (code === "ZAR") {
    return formatZarDisplay(amount);
  }
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${amount} ${code}`;
  }
}

function resolveDisplayStatus(
  apiStatus: string | undefined,
  expiresAtIso: string | undefined
): PaymentLinkStatus {
  const s = (apiStatus || "").toLowerCase();
  if (s === "paid" || s === "completed" || s === "confirmed") {
    return "paid";
  }
  if (s === "expired" || s === "cancelled") {
    return "expired";
  }
  if (expiresAtIso) {
    const exp = parseISO(expiresAtIso);
    if (isValid(exp) && Date.now() > exp.getTime()) {
      return "expired";
    }
  }
  return "unpaid";
}

function recipientDisplayName(link: ApiPayLink): string {
  const parts = [link.recipientName, link.recipientSurname]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);
  if (parts.length > 0) {
    return parts.join(" ");
  }
  const desc = link.description?.trim();
  if (desc) {
    return desc;
  }
  return link.payLinkId || link._id || "Payment link";
}

function createdLabelFromApi(createdAt?: string): string {
  if (!createdAt) {
    return "—";
  }
  const d = parseISO(createdAt);
  if (!isValid(d)) {
    return "—";
  }
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

export function mapApiPayLinkToRow(link: ApiPayLink): PaymentLinkRow {
  const amountNum = parseApiAmount(link.amount);
  const currency = link.currency || "ZAR";
  const createdAt = link.createdAt;
  const createdMs = createdAt
    ? (() => {
        const d = parseISO(createdAt);
        return isValid(d) ? d.getTime() : undefined;
      })()
    : undefined;

  return {
    id: link.payLinkId || link._id,
    backendId: link._id,
    name: recipientDisplayName(link),
    createdLabel: createdLabelFromApi(createdAt),
    createdAtMs: createdMs,
    createdAt,
    amountFormatted: formatAmountDisplay(amountNum, currency),
    amountNumeric: amountNum,
    reference: (link.reference || link.description || "—").trim() || "—",
    status: resolveDisplayStatus(link.status, link.expiresAt),
    shareUrl: link.payLinkUrl || undefined,
  };
}
